import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// Davis-Bacon (Kloepfer K9-21): Settings → Timesheets → Codes & Lists →
// Davis-Bacon. Pick a Davis-Bacon job (a project with "Davis-Bacon job"
// ticked in its details), type in its wage rates from the wage
// determination — trade, base and fringe per hour — and give each labor type
// the federal trade it's paid as. The week's certified payroll (Timesheets →
// Export) prints them. Every save answers with the whole tab
// (/timesheets/davis_bacon), so nothing reloads.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

const money = (v) => (v === null || v === undefined ? "" : Number(v).toFixed(2))

const RATE = "w-24 px-2 py-1 border border-gray-300 rounded-lg bg-white text-[13px] text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
const TEXT = "w-full min-w-[180px] px-2 py-1 border border-gray-300 rounded-lg bg-white text-[13px] focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
const TH = "px-2 py-1.5 font-medium whitespace-nowrap"
const TD = "px-2 py-1.5 whitespace-nowrap"
const BTN = "px-3 py-1.5 rounded-lg text-[13px] cursor-pointer whitespace-nowrap"

export default class extends Controller {
  static targets = ["pane"]
  static values = { url: String, text: Object }

  connect() {
    this.payload = null
    this.jobId = null
  }

  t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => o?.[k], this.textValue) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`%{${k}}`, v)
    return s
  }

  // Loaded each time the tab opens, so a job switched on since shows up.
  async show() {
    if (!this.hasPaneTarget) return
    if (!this.payload) this.paneTarget.innerHTML = `<p class="text-[13px] text-gray-400">${esc(this.t("loading"))}</p>`
    const data = await this._request("GET", this._jobUrl(this.urlValue))
    if (!data) return
    const first = !this.payload
    this._take(data)
    if (first) track("Davis-Bacon Wages Viewed", { jobs: data.jobs.length, labor_types: data.labor_types.length })
  }

  async pickJob(e) {
    this.jobId = Number(e.currentTarget.value)
    const data = await this._request("GET", this._jobUrl(this.urlValue))
    if (data) this._take(data)
  }

  // A trade's name or rate changed in its row: saved on change (Enter or leaving the box).
  async saveWage(e) {
    const el = e.currentTarget
    const field = el.dataset.field
    const value = el.value.trim()
    if (field !== "trade" && !this._rateOk(value, el)) return
    if (field === "trade" && value === "") {
      el.value = el.defaultValue
      return showToast(this.t("trade_needed"), "alert")
    }
    const data = await this._request("PATCH", `${this.urlValue}/wages/${el.dataset.id}`, { [field]: value })
    if (data) {
      this._take(data)
      showToast(this.t("saved"))
    }
  }

  async addWage() {
    const row = this.paneTarget.querySelector("[data-db-new]")
    const trade = row.querySelector('[data-field="trade"]')
    const base = row.querySelector('[data-field="base_rate"]')
    const fringe = row.querySelector('[data-field="fringe_rate"]')
    if (trade.value.trim() === "") {
      trade.focus()
      return showToast(this.t("trade_needed"), "alert")
    }
    if (!this._rateOk(base.value.trim(), base) || !this._rateOk(fringe.value.trim(), fringe)) return
    const data = await this._request("POST", `${this.urlValue}/wages`, {
      project_id: this.jobId, trade: trade.value.trim(), base_rate: base.value.trim(), fringe_rate: fringe.value.trim()
    })
    if (!data) return
    this._take(data)
    this.paneTarget.querySelector('[data-db-new] [data-field="trade"]')?.focus()
    showToast(this.t("saved"))
  }

  addOnEnter(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      this.addWage()
    }
  }

  async removeWage(e) {
    const data = await this._request("DELETE", `${this.urlValue}/wages/${e.currentTarget.dataset.id}`)
    if (data) {
      this._take(data)
      showToast(this.t("removed"))
    }
  }

  async saveTrade(e) {
    const el = e.currentTarget
    const data = await this._request("PATCH", `${this.urlValue}/labor_types/${el.dataset.id}`, { trade: el.value.trim(), project_id: this.jobId })
    if (data) {
      this._take(data)
      showToast(this.t("saved"))
    }
  }

  blurOnEnter(e) {
    if (e.key === "Enter") e.currentTarget.blur()
  }

  // ── Drawing ────────────────────────────────────────────────────────

  _take(data) {
    this.payload = data
    this.jobId = data.job_id
    this._render()
  }

  _render() {
    const p = this.payload
    const head = `<div>
        <h4 class="text-[15px] font-semibold text-gray-900">${esc(this.t("title"))}</h4>
        <p class="text-[13px] text-gray-500">${esc(this.t("help"))}</p>
      </div>`
    if (!p.jobs.length) {
      this.paneTarget.innerHTML = `<div class="space-y-4" data-db-empty>${head}
        <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center">
          <p class="text-[13px] font-semibold text-gray-900">${esc(this.t("none_title"))}</p>
          <p class="mt-1 text-[13px] text-gray-500">${esc(this.t("none_help"))}</p>
        </div>
        ${this._tradesTable(p)}
      </div>`
      return
    }
    const job = p.jobs.find((j) => j.id === p.job_id)
    const options = p.jobs.map((j) => `<option value="${j.id}"${j.id === p.job_id ? " selected" : ""}>${esc(j.name)}</option>`).join("")
    const wd = job?.wage_determination
      ? `<span class="text-[13px] text-gray-600">${esc(this.t("wd", { number: job.wage_determination }))}</span>`
      : `<span class="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[13px] text-amber-900" data-db-no-wd>${esc(this.t("wd_missing"))}</span>`
    this.paneTarget.innerHTML = `
      <div class="space-y-5" data-db-job="${p.job_id}">
        ${head}
        <div class="flex flex-wrap items-center gap-3">
          <label for="ts-db-job" class="text-[13px] font-medium text-gray-700">${esc(this.t("job"))}</label>
          <select id="ts-db-job" data-action="change->time-davis-bacon#pickJob"
                  class="flex-1 max-w-md py-1 pl-2 pr-7 border border-gray-300 rounded-lg text-[13px] bg-white text-gray-700 cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand">${options}</select>
          ${wd}
        </div>
        ${this._wagesTable(p)}
        ${this._tradesTable(p)}
      </div>`
  }

  _wagesTable(p) {
    const rate = (w, field) => `<input type="text" inputmode="decimal" value="${esc(money(w[field]))}" placeholder="0.00" aria-label="${esc(this.t(`col_${field === "base_rate" ? "base" : "fringe"}`))} — ${esc(w.trade)}"
        data-id="${w.id}" data-field="${field}" data-action="change->time-davis-bacon#saveWage keydown->time-davis-bacon#blurOnEnter" class="${RATE}">`
    const rows = p.wages.map((w) => `<tr data-db-wage="${w.id}">
        <td class="${TD}"><input type="text" value="${esc(w.trade)}" maxlength="120" aria-label="${esc(this.t("col_trade"))}"
            data-id="${w.id}" data-field="trade" data-action="change->time-davis-bacon#saveWage keydown->time-davis-bacon#blurOnEnter" class="${TEXT}"></td>
        <td class="${TD} text-right">${rate(w, "base_rate")}</td>
        <td class="${TD} text-right">${rate(w, "fringe_rate")}</td>
        <td class="${TD} text-right tabular-nums text-gray-900" data-db-total>${w.total_rate === null ? "—" : `$ ${money(w.total_rate)}`}</td>
        <td class="${TD} text-right">
          <button type="button" data-action="click->time-davis-bacon#removeWage" data-id="${w.id}" title="${esc(this.t("remove"))}" aria-label="${esc(this.t("remove"))} — ${esc(w.trade)}"
                  class="p-1 rounded text-gray-400 hover:text-rose-700 hover:bg-rose-50 cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4" aria-hidden="true"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/></svg>
          </button>
        </td>
      </tr>`).join("")
    const empty = p.wages.length ? "" : `<tr><td colspan="5" class="${TD} text-gray-500">${esc(this.t("no_wages"))}</td></tr>`
    const add = `<tr data-db-new class="bg-gray-50">
        <td class="${TD}"><input type="text" maxlength="120" placeholder="${esc(this.t("trade_ph"))}" aria-label="${esc(this.t("col_trade"))}" data-field="trade" data-action="keydown->time-davis-bacon#addOnEnter" class="${TEXT}"></td>
        <td class="${TD} text-right"><input type="text" inputmode="decimal" placeholder="0.00" aria-label="${esc(this.t("col_base"))}" data-field="base_rate" data-action="keydown->time-davis-bacon#addOnEnter" class="${RATE}"></td>
        <td class="${TD} text-right"><input type="text" inputmode="decimal" placeholder="0.00" aria-label="${esc(this.t("col_fringe"))}" data-field="fringe_rate" data-action="keydown->time-davis-bacon#addOnEnter" class="${RATE}"></td>
        <td class="${TD}" colspan="2"><button type="button" data-action="click->time-davis-bacon#addWage" class="${BTN} bg-brand text-white font-semibold hover:bg-brand-dark">${esc(this.t("add"))}</button></td>
      </tr>`
    return `<div class="space-y-1.5">
      <h5 class="text-[13px] font-semibold text-gray-900">${esc(this.t("wages_title"))}</h5>
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          <thead class="bg-gray-50 text-gray-500"><tr>
            <th class="${TH} text-left">${esc(this.t("col_trade"))}</th><th class="${TH} text-right">${esc(this.t("col_base"))}</th>
            <th class="${TH} text-right">${esc(this.t("col_fringe"))}</th><th class="${TH} text-right">${esc(this.t("col_total"))}</th><th class="${TH}"></th>
          </tr></thead>
          <tbody class="divide-y divide-gray-200">${rows}${empty}${add}</tbody>
        </table>
      </div>
    </div>`
  }

  _tradesTable(p) {
    const trades = p.trades.map((t) => `<option value="${esc(t)}"></option>`).join("")
    const wageTrades = new Set(p.wages.map((w) => w.trade.toLowerCase()))
    const rows = p.labor_types.map((lt) => {
      const off = p.jobs.length && lt.trade && !wageTrades.has(lt.trade.toLowerCase())
      return `<tr data-db-labor-type="${lt.id}">
        <td class="${TD} text-gray-900">${esc(lt.name)}</td>
        <td class="${TD} text-right tabular-nums text-gray-600">${lt.people}</td>
        <td class="${TD}">
          <input type="text" value="${esc(lt.trade || "")}" maxlength="120" list="ts-db-trades" placeholder="${esc(this.t("trade_ph"))}" aria-label="${esc(this.t("col_db_trade"))} — ${esc(lt.name)}"
                 data-id="${lt.id}" data-action="change->time-davis-bacon#saveTrade keydown->time-davis-bacon#blurOnEnter" class="${TEXT}">
          ${off ? `<span class="block mt-0.5 text-[13px] text-amber-700">${esc(this.t("no_match"))}</span>` : ""}
        </td>
      </tr>`
    }).join("")
    const body = p.labor_types.length ? rows : `<tr><td colspan="3" class="${TD} text-gray-500">${esc(this.t("no_labor_types"))}</td></tr>`
    return `<div class="space-y-1.5">
      <h5 class="text-[13px] font-semibold text-gray-900">${esc(this.t("trades_title"))}</h5>
      <p class="text-[13px] text-gray-500">${esc(this.t("trades_help"))}</p>
      <datalist id="ts-db-trades">${trades}</datalist>
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-[13px]">
          <thead class="bg-gray-50 text-gray-500"><tr>
            <th class="${TH} text-left">${esc(this.t("col_labor_type"))}</th><th class="${TH} text-right">${esc(this.t("col_people"))}</th><th class="${TH} text-left">${esc(this.t("col_db_trade"))}</th>
          </tr></thead>
          <tbody class="divide-y divide-gray-200">${body}</tbody>
        </table>
      </div>
    </div>`
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _jobUrl(base) {
    return this.jobId ? `${base}?project_id=${this.jobId}` : base
  }

  _rateOk(value, el) {
    const raw = value.replace(/[$,]/g, "")
    if (raw === "" || Number(raw) >= 0) {
      el.classList.remove("border-red-400")
      return true
    }
    el.classList.add("border-red-400")
    showToast(this.t("invalid"), "alert")
    return false
  }

  async _request(method, url, body) {
    const headers = { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Accept": "application/json" }
    if (body) headers["Content-Type"] = "application/json"
    try {
      const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined, credentials: "same-origin" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showToast(data.error || this.t("error"), "alert")
        return null
      }
      return data
    } catch (_) {
      showToast(this.t("error"), "alert")
      return null
    }
  }
}
